// Filmstrip (§8, §10): virtualized (only visible cells mount — 2,000 photos scroll smoothly), lazy
// thumbnails from the decode pool + IndexedDB cache, badges (stars WITH numbers, reject, edited,
// background removed, sidecar mismatch), click / shift-click / ctrl-click selection, filter menu.
import { memo, useEffect, useRef, useState } from 'react';
import { useLibrary, visiblePhotos, type Filter, type LibraryPhoto } from '../../state/library';
import { getThumbnail } from '../../io/thumbnails';
import { useT } from '../../i18n';
import { useSimple } from '../../state/simple';
import s from './Filmstrip.module.css';

const CELL = 112; // px per cell including the gap

// Thumbnail URLs, shared across re-renders and remounts.
const urls = new Map<string, string>();
const pending = new Map<string, Promise<string>>();
let active = 0;
const queue: (() => void)[] = [];
function limited<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const go = () => {
      active++;
      fn()
        .then(resolve, reject)
        .finally(() => {
          active--;
          queue.shift()?.();
        });
    };
    if (active < 4) go();
    else queue.push(go);
  });
}
function thumbUrl(p: LibraryPhoto): Promise<string> {
  const hit = urls.get(p.id);
  if (hit) return Promise.resolve(hit);
  let pr = pending.get(p.id);
  if (!pr) {
    pr = limited(() => getThumbnail(p.file)).then((r) => {
      const u = URL.createObjectURL(r.blob);
      urls.set(p.id, u);
      pending.delete(p.id);
      return u;
    });
    pending.set(p.id, pr);
  }
  return pr;
}

const Cell = memo(function Cell({
  p,
  x,
  selected,
  active,
}: {
  p: LibraryPhoto;
  x: number;
  selected: boolean;
  active: boolean;
}) {
  const t = useT();
  const [url, setUrl] = useState<string | null>(urls.get(p.id) ?? null);
  useEffect(() => {
    let alive = true;
    if (!url)
      thumbUrl(p).then(
        (u) => alive && setUrl(u),
        () => {},
      );
    return () => {
      alive = false;
    };
  }, [p, url]);
  const label = [
    p.name,
    p.rating ? t('stars', { n: p.rating }) : '',
    p.rejected ? t('rejected') : '',
    p.edited ? t('edited') : '',
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <button
      className={`${s.cell} ${selected ? s.selected : ''} ${active ? s.active : ''} ${p.rejected ? s.rejected : ''}`}
      style={{ left: x }}
      aria-label={label}
      aria-pressed={selected}
      data-testid="film-cell"
      data-name={p.name}
      onClick={(e) => useLibrary.getState().click(p.id, { shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey })}
    >
      {url ? <img src={url} alt="" draggable={false} /> : <span className={s.placeholder} />}
      <span className={s.badges}>
        {p.rating > 0 && <span className={s.stars}>★{p.rating}</span>}
        {p.rejected && (
          <span className={s.reject} title={t('rejected')}>
            ✕
          </span>
        )}
        {p.edited && (
          <span className={s.edited} title={t('edited')}>
            ●
          </span>
        )}
        {p.bgRemoved && <span title={t('bgRemoved')}>✂</span>}
        {p.sidecarLocked && (
          <span className={s.warn} title={t('sidecarLocked')}>
            🔒
          </span>
        )}
        {p.sidecarMismatch && (
          <span className={s.warn} title={t('sidecarMismatch')}>
            ⚠
          </span>
        )}
      </span>
    </button>
  );
});

export function Filmstrip() {
  const t = useT();
  const photos = useLibrary((st) => st.photos);
  const filter = useLibrary((st) => st.filter);
  const selected = useLibrary((st) => st.selected);
  const activeId = useLibrary((st) => st.activeId);
  const vis = visiblePhotos({ photos, filter });
  const scroller = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ left: 0, width: 1200 });

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setView({ left: el.scrollLeft, width: el.clientWidth }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep the active photo in view.
  useEffect(() => {
    const el = scroller.current;
    const i = vis.findIndex((p) => p.id === activeId);
    if (!el || i < 0) return;
    const x = i * CELL;
    if (x < el.scrollLeft) el.scrollLeft = x;
    else if (x + CELL > el.scrollLeft + el.clientWidth) el.scrollLeft = x + CELL - el.clientWidth;
  }, [activeId, vis]);

  const simple = useSimple((st) => st.mode === 'simple');
  const first = Math.max(0, Math.floor(view.left / CELL) - 4);
  const last = Math.min(vis.length, Math.ceil((view.left + view.width) / CELL) + 4);
  const options: { v: Filter; label: string }[] = [
    { v: 'all', label: t('filterAll') },
    { v: 'picks', label: t('filterPicks') },
    ...([1, 2, 3, 4, 5] as const).map((n) => ({ v: n as Filter, label: t('filterStars', { n }) })),
    { v: 'edited', label: t('filterEdited') },
    { v: 'rejected', label: t('filterRejected') },
  ];

  return (
    <div className={s.wrap}>
      <div className={s.bar}>
        {/* The culling filter is a Pro tool; in Simple mode "Filtro" would clash with step 3 "Filtros" (S21). */}
        <label hidden={simple}>
          {t('filter')}{' '}
          <select
            value={String(filter)}
            data-testid="filter"
            onChange={(e) =>
              useLibrary
                .getState()
                .setFilter((/^\d$/.test(e.target.value) ? Number(e.target.value) : e.target.value) as Filter)
            }
          >
            {options.map((o) => (
              <option key={String(o.v)} value={String(o.v)}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <span className={s.count}>
          {vis.length}/{photos.length}
          {selected.size > 1 ? ` · ${t('selectedCount', { n: selected.size })}` : ''}
        </span>
      </div>
      <div
        ref={scroller}
        className={s.scroller}
        data-testid="filmstrip"
        onScroll={(e) => setView({ left: e.currentTarget.scrollLeft, width: e.currentTarget.clientWidth })}
      >
        <div className={s.track} style={{ width: vis.length * CELL }}>
          {vis.slice(first, last).map((p, k) => (
            <Cell
              key={p.id}
              p={p}
              x={(first + k) * CELL}
              selected={selected.has(p.id)}
              active={p.id === activeId}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
